import { ActivatedRouteSnapshot } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { environment } from '../../../../../environments/environment';
import { addDays, addMonths, addWeeks, addYears, isBefore, isAfter, parse } from 'date-fns';
import { getInitialFilter, PriceFilter } from '../../stores/price.store';

/** The earliest date for which price data is available. */
export const PRICE_DATA_MIN_DATE = environment.analysis.price.minDate;

/** Returns the latest allowed date (today + max years, the max prognosis horizon). */
export const getPriceDataMaxDate = (): Date =>
  addYears(new Date(), environment.analysis.price.maxYears);

/** Checks whether navigating to the previous period would stay within the allowed date range. */
export const canNavigatePrevious = (
  date: Date,
  granularity: PriceFilter['granularity'],
): boolean => {
  const previousDate = getOffsetDate(date, granularity, -1);
  return !isBefore(previousDate, PRICE_DATA_MIN_DATE);
};

/** Checks whether navigating to the next period would stay within the allowed date range. */
export const canNavigateNext = (date: Date, granularity: PriceFilter['granularity']): boolean => {
  const nextDate = getOffsetDate(date, granularity, 1);
  return !isAfter(nextDate, getPriceDataMaxDate());
};

/** Clamps a date to the allowed [min, max] range. */
export const clampDateToRange = (date: Date): Date => {
  if (isBefore(date, PRICE_DATA_MIN_DATE)) return PRICE_DATA_MIN_DATE;
  const max = getPriceDataMaxDate();
  if (isAfter(date, max)) return max;
  return date;
};

const getOffsetDate = (
  date: Date,
  granularity: PriceFilter['granularity'],
  direction: 1 | -1,
): Date => {
  switch (granularity) {
    case 'YEAR':
      return addYears(date, direction);
    case 'MONTH':
      return addMonths(date, direction);
    case 'WEEK':
      return addWeeks(date, direction);
    case 'DAY':
      return addDays(date, direction);
  }
};

/** Checks whether a query param represents a valid integer year offered by the historic year selector. */
export const isValidHistoricYear = (year: string | null | undefined): year is string => {
  if (typeof year !== 'string' || year === '') {
    return false;
  }
  const parsed = Number(year);
  const minYear = PRICE_DATA_MIN_DATE.getFullYear();
  const lastYear = new Date().getFullYear() - 1;

  return (
    Number.isInteger(parsed) && String(parsed) === year && parsed >= minYear && parsed <= lastYear
  );
};

export const getFilterFromQueryParams = (route: ActivatedRouteSnapshot): PriceFilter => {
  const granularity = route.queryParamMap.get('granularity');
  const date = route.queryParamMap.get('date');
  const showEurMwh = route.queryParamMap.get('showEurMwh');
  const view = route.queryParamMap.get('view');
  const compareHistoricYear = route.queryParamMap.get('compareHistoricYear');

  const filter: PriceFilter = {
    ...getInitialFilter(),
    showEurMwh: showEurMwh === 'true',
  };

  if (
    granularity === 'YEAR' ||
    granularity === 'MONTH' ||
    granularity === 'WEEK' ||
    granularity === 'DAY'
  ) {
    filter.granularity = granularity;
  }

  if (date) {
    try {
      const parsedDate = parse(date, 'yyyy-MM-dd', new Date());
      if (isNaN(parsedDate.getTime())) {
        throw new Error('Invalid date format for query parameter "date".');
      }
      filter.date = clampDateToRange(parsedDate);
    } catch (error) {
      Sentry.logger.error(
        Sentry.logger.fmt`Price graph: Invalid date format for query parameter "date": ${date}`,
        { error },
      );
    }
  }

  if (view === 'graph' || view === 'table') {
    filter.view = view;
  }

  if (isValidHistoricYear(compareHistoricYear)) {
    filter.compareHistoricYear = compareHistoricYear;
  }

  return filter;
};
